import gql from 'graphql-tag';
import { GraphQLFormattedError } from 'graphql';
import {
  ApolloClient,
  NormalizedCacheObject,
  ApolloError,
  TypedDocumentNode,
} from '@apollo/client';
import { FastifyLoggerInstance } from 'fastify';
import {
  costGuideComparisonsVariables,
  costGuideComparisons as costGuideComparisonsQueryType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons as costGuideComparisonsType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsError_errors as costGuideComparisonsErrorsType,
} from '@/__generated__/CostGuideComparisons';
import { apolloClientQuery } from './utils';

const COST_GUIDE_COMPARISONS: TypedDocumentNode<
  costGuideComparisonsQueryType,
  costGuideComparisonsVariables
> = gql`
  query costGuideComparisons($costGuideComparisonsId: ID!) {
    costGuideComparisons(id: $costGuideComparisonsId) {
      __typename
      ... on CostGuideComparisonsSuccess {
        costGuideComparisons {
          geographicRateComparison {
            hourlyRateComparisons {
              comparedAgainstRate {
                amount
                currencyCode
              }
              national {
                comparison {
                  hourlyRate {
                    amount
                    currencyCode
                  }
                  percentage {
                    relativePosition
                    value
                  }
                }
                country
              }
              state {
                comparison {
                  hourlyRate {
                    amount
                    currencyCode
                  }
                  percentage {
                    relativePosition
                    value
                  }
                }
                stateCode
              }
            }
            locationComparisons {
              location {
                city
                state {
                  name
                  stateCode
                }
              }
              rates {
                averageRate {
                  averageHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
                lastUpdatedDate
                maximumRate {
                  maximumHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
              }
              segmentationUrl
              costPageUrl
            }
          }
          mostAffordableRateComparison {
            locationComparisons {
              location {
                city
                state {
                  name
                  stateCode
                }
              }
              rates {
                averageRate {
                  averageHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
                lastUpdatedDate
                maximumRate {
                  maximumHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
              }
              segmentationUrl
              costPageUrl
            }
          }
          mostExpensiveRateComparison {
            locationComparisons {
              location {
                city
                state {
                  name
                  stateCode
                }
              }
              rates {
                averageRate {
                  averageHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  averageWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
                lastUpdatedDate
                maximumRate {
                  maximumHourlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumMonthlyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                  maximumWeeklyRate {
                    rate {
                      amount
                      currencyCode
                    }
                    rateDuration
                  }
                }
              }
              segmentationUrl
              costPageUrl
            }
          }
        }
      }
      ... on CostGuideComparisonsError {
        __typename
        errors {
          __typename
          message
        }
      }
    }
  }
`;

type CostGuideComparisonsQuery = (
  client: ApolloClient<NormalizedCacheObject>,
  log: FastifyLoggerInstance,
  variables: costGuideComparisonsVariables
) => Promise<
  | {
      costGuideComparisons: costGuideComparisonsType | null;
      error: null;
    }
  | {
      costGuideComparisons: null;
      error: ApolloError | readonly GraphQLFormattedError[] | costGuideComparisonsErrorsType[];
    }
>;

const costGuideComparisons: CostGuideComparisonsQuery = async (client, log, variables) => {
  const { queryResponse, error } = await apolloClientQuery(
    client,
    COST_GUIDE_COMPARISONS,
    variables
  );

  // unknown error
  if (error) {
    return {
      costGuideComparisons: null,
      error,
    };
  }

  const { data, errors, error: responseError } = queryResponse;

  // GraphQL errors that occur while processing a GraphQL operation (resulting in built-in error codes such as INTERNAL_SERVER_ERROR)
  if (errors && errors.length > 0) {
    return {
      costGuideComparisons: null,
      error: errors,
    };
  }

  // Coverage for 'error' property in queryResponse
  if (responseError) {
    return {
      costGuideComparisons: null,
      error: responseError,
    };
  }

  // known error
  if (data.costGuideComparisons.__typename === 'CostGuideComparisonsError') {
    log.error(`[GraphQL errors]: ${errors}`);

    return {
      costGuideComparisons: null,
      error: data.costGuideComparisons.errors,
    };
  }

  return { costGuideComparisons: data.costGuideComparisons.costGuideComparisons, error: null };
};

export { COST_GUIDE_COMPARISONS, costGuideComparisons };
